import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import clsx from 'clsx';
import { InputText } from 'primereact/inputtext';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { GetInputClassName } from '../../_utils/get-input-class-name.js';
import { getPlaceholder } from '../../_utils/get-placeholder.js';
import { InputWrapper } from '../InputWrapper/InputWrapper.js';

function InputComponent(props, ref) {
  const {
      name,
      className,
      wrapperClassName,
      required,
      onFocus,
      errorMessage,
      onBlur,
      debounce,
      bulkMode,
      labelHidden: __,
      labelClassName: ___,
      horizontal: ____,
      label: _____,
      labelSuffix: ______,
      suffixIcon,
      prefixIcon,
      outlined
    } = props,
    restProps = __rest(props, ["name", "className", "wrapperClassName", "required", "onFocus", "errorMessage", "onBlur", "debounce", "bulkMode", "labelHidden", "labelClassName", "horizontal", "label", "labelSuffix", "suffixIcon", "prefixIcon", "outlined"]);
  const placeholder = getPlaceholder(props);
  const formContext = useFormContext();
  const debounceTimer = useRef(null);
  const [focus, setFocus] = useState(false);
  const suffixIconTemplate = suffixIcon ? jsx("span", {
    className: "p-inputgroup-addon",
    children: suffixIcon
  }) : null;
  const prefixIconTemplate = prefixIcon ? jsx("span", {
    className: "p-inputgroup-addon",
    children: prefixIcon
  }) : null;
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);
  const isControlled = typeof restProps.value !== 'undefined';
  if (isControlled || !formContext) {
    return jsx(InputWrapper, Object.assign({
      wrapperClassName: clsx(className, wrapperClassName, outlined && 'input-wrapper--outlined')
    }, props, {
      children: jsxs("div", {
        className: clsx('p-inputgroup flex-1', restProps.disabled && 'p-inputgroup--disabled'),
        children: [prefixIconTemplate, jsx(InputText, Object.assign({
          ref: ref
        }, restProps, {
          name: name,
          onChange: e => {
            const {
              onChange
            } = restProps;
            if (!onChange) {
              return;
            }
            if (!debounce) {
              onChange(e);
              return;
            }
            if (debounceTimer.current) {
              clearTimeout(debounceTimer.current);
            }
            debounceTimer.current = setTimeout(() => onChange(e), debounce);
          },
          placeholder: placeholder,
          onBlur: onBlur,
          className: GetInputClassName({
            basicClassName: 'flex grow',
            invalidState: Boolean(errorMessage)
          })
        })), suffixIconTemplate]
      })
    }));
  }
  return jsx(Controller, {
    name: name,
    control: formContext.control,
    rules: {
      required: required && !bulkMode
    },
    render: _ref => {
      let {
        field,
        fieldState
      } = _ref;
      return jsx(InputWrapper, Object.assign({
        field: field,
        fieldState: fieldState,
        focus: focus,
        wrapperClassName: clsx(wrapperClassName, outlined && 'input-wrapper--outlined')
      }, props, {
        children: jsxs("div", {
          className: clsx('p-inputgroup flex-1', restProps.disabled && 'p-inputgroup--disabled'),
          children: [prefixIconTemplate, jsx(InputText, Object.assign({
            className: GetInputClassName({
              basicClassName: 'flex grow',
              invalidState: fieldState.invalid
            }),
            variant: "outlined"
          }, restProps, field, {
            name: name,
            placeholder: placeholder,
            value: (field === null || field === void 0 ? void 0 : field.value) || props.value || '',
            ref: ref,
            onBlurCapture: () => setFocus(false),
            onChange: e => {
              if (restProps.onChange) {
                restProps.onChange(e);
              }
              field.onChange(e);
            },
            onFocus: e => {
              setFocus(true);
              if (onFocus) {
                onFocus(e);
              }
            },
            onBlur: e => {
              field.onBlur();
              if (onBlur) {
                onBlur(e);
              }
            }
          })), suffixIconTemplate]
        })
      }));
    }
  });
}
const Input = /*#__PURE__*/forwardRef(InputComponent);

export { Input };
